import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Backpack, Gift, Loader2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { apiService } from '@/services/api'
import { itemTypeLabel } from '@/lib/itemTypes'
import type { GameItem } from '@/types'

// Instância no inventário (resposta enriquecida do backend)
export interface InventoryEntry {
  id?: string
  itemId: string
  quantity: number
  name?: string
  description?: string
  icon?: string
  itemType?: string
  removed?: boolean
}

// Personagem (subset usado aqui — camelCase como na GameSession)
export interface PanelCharacter {
  id: string
  name: string
  userId: string
  isNpc?: boolean
}

interface ItemsPanelProps {
  tableId: string
  characters: PanelCharacter[]
  currentUserId: string
  isMaster: boolean
  subscribe: (fn: (data: any) => void) => () => void
}

export function ItemsPanel({ tableId, characters, currentUserId, isMaster, subscribe }: ItemsPanelProps) {
  const [catalog, setCatalog] = useState<GameItem[]>([])
  const [inventory, setInventory] = useState<InventoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [granting, setGranting] = useState<string | null>(null)

  // Personagem em foco: para o mestre é o selecionado; para o jogador é o próprio.
  const ownCharacter = characters.find((c) => c.userId === currentUserId && !c.isNpc)
  const [selectedCharId, setSelectedCharId] = useState<string>('')

  const viewedCharId = isMaster ? selectedCharId : (ownCharacter?.id ?? '')
  const viewedCharIdRef = useRef(viewedCharId)
  viewedCharIdRef.current = viewedCharId

  // Mestre: escolher um personagem por padrão (o primeiro)
  useEffect(() => {
    if (isMaster && !selectedCharId && characters.length > 0) {
      setSelectedCharId(characters[0].id)
    }
  }, [isMaster, characters, selectedCharId])

  // Catálogo (só o mestre precisa dele para conceder)
  useEffect(() => {
    if (!isMaster || !tableId) return
    let cancelled = false
    apiService.getGameConfig(tableId)
      .then((cfg) => { if (!cancelled) setCatalog(cfg?.items ?? []) })
      .catch(() => { if (!cancelled) setCatalog([]) })
    return () => { cancelled = true }
  }, [isMaster, tableId])

  // Inventário do personagem em foco
  useEffect(() => {
    if (!viewedCharId) { setInventory([]); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    apiService.getCharacterInventory(viewedCharId)
      .then((data) => { if (!cancelled) setInventory(data as InventoryEntry[]) })
      .catch(() => { if (!cancelled) toast.error('Erro ao carregar inventário') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [viewedCharId])

  // Tempo real: aplica inventory_update quando é do personagem em foco
  useEffect(() => {
    const handler = (data: any) => {
      if (data?.type !== 'inventory_update') return
      if (data.characterId !== viewedCharIdRef.current) return
      const e = data.entry as InventoryEntry
      setInventory((prev) => {
        if (e.removed || e.quantity <= 0) return prev.filter((x) => x.itemId !== e.itemId)
        const idx = prev.findIndex((x) => x.itemId === e.itemId)
        if (idx >= 0) {
          const cp = [...prev]
          cp[idx] = { ...cp[idx], ...e }
          return cp
        }
        return [e, ...prev]
      })
    }
    const unsubscribe = subscribe(handler)
    return unsubscribe
  }, [subscribe])

  const handleGrant = useCallback(async (item: GameItem) => {
    if (!viewedCharId) return
    setGranting(item.id)
    try {
      await apiService.grantItem(viewedCharId, item.id, 1)
      // O broadcast atualiza a lista; sem WS (fallback), atualiza otimista
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao conceder item')
    } finally {
      setGranting(null)
    }
  }, [viewedCharId])

  const handleRevoke = useCallback(async (entry: InventoryEntry) => {
    if (!viewedCharId) return
    try {
      await apiService.revokeItem(viewedCharId, entry.itemId)
      setInventory((prev) => prev.filter((x) => x.itemId !== entry.itemId))
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao remover item')
    }
  }, [viewedCharId])

  const viewedName = characters.find((c) => c.id === viewedCharId)?.name

  // ---- Jogador sem personagem ----
  if (!isMaster && !ownCharacter) {
    return (
      <div className="h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col items-center justify-center text-center p-6">
        <div className="w-12 h-12 bg-game-tertiary dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-3">
          <Backpack className="h-6 w-6 text-game-muted" />
        </div>
        <p className="text-sm text-game-muted">Você ainda não tem personagem nesta mesa.</p>
        <p className="text-xs text-game-muted mt-1">Seus itens aparecerão aqui quando tiver um.</p>
      </div>
    )
  }

  return (
    <div className="h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col">
      <div className="p-4 border-b border-game-border-primary dark:border-zinc-800">
        <p className="text-xs uppercase tracking-[0.2em] text-game-muted dark:text-zinc-500 mb-1">Inventário</p>
        {isMaster ? (
          <>
            <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">Itens dos personagens</h3>
            <select
              value={selectedCharId}
              onChange={(e) => setSelectedCharId(e.target.value)}
              className="mt-2 flex h-9 w-full rounded-md border border-game-border-primary dark:border-zinc-700 bg-white dark:bg-zinc-800/80 px-3 py-1 text-sm text-game-primary dark:text-zinc-100"
            >
              {characters.length === 0 && <option value="">Nenhum personagem</option>}
              {characters.map((c) => (
                <option key={c.id} value={c.id}>{c.isNpc ? `${c.name} (NPC)` : c.name}</option>
              ))}
            </select>
          </>
        ) : (
          <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">
            Itens de {ownCharacter?.name}
          </h3>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
            </div>
          )}

          {/* Inventário do personagem em foco */}
          {!loading && (
            <div className="space-y-2">
              {inventory.length === 0 ? (
                <div className="text-center py-8 px-4 text-game-muted">
                  <div className="w-12 h-12 mx-auto bg-game-tertiary dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-3">
                    <Backpack className="h-6 w-6 text-game-muted" />
                  </div>
                  <p className="text-sm">Nenhum item ainda</p>
                  {isMaster && <p className="text-xs mt-1">Conceda itens do catálogo abaixo.</p>}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {inventory.map((entry) => (
                    <div
                      key={entry.itemId}
                      className="group relative rounded-xl border border-game-border-primary dark:border-zinc-800 bg-white dark:bg-zinc-800/40 p-3 flex flex-col items-center text-center"
                      title={entry.description || ''}
                    >
                      {entry.quantity > 1 && (
                        <span className="absolute top-1 right-1.5 min-w-[18px] h-[18px] px-1 text-[11px] font-semibold rounded-full bg-violet-600 text-white flex items-center justify-center">
                          {entry.quantity}
                        </span>
                      )}
                      <div className="text-3xl mb-1">{entry.icon || '🎁'}</div>
                      <div className="text-xs font-medium text-game-primary dark:text-zinc-100 line-clamp-2">{entry.name}</div>
                      <div className="text-[10px] text-game-muted mt-0.5">{itemTypeLabel(entry.itemType || 'trofeu')}</div>
                      {isMaster && (
                        <button
                          onClick={() => handleRevoke(entry)}
                          className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-game-muted hover:text-rose-400"
                          title="Remover do personagem"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Catálogo (só o mestre): conceder ao personagem em foco */}
          {isMaster && (
            <div className="pt-2 border-t border-game-border-primary dark:border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="h-4 w-4 text-violet-500" />
                <h4 className="text-sm font-semibold text-game-primary dark:text-zinc-100">Catálogo</h4>
              </div>
              {catalog.length === 0 ? (
                <p className="text-xs text-game-muted py-2">
                  Nenhum item no catálogo. Crie itens em "Itens da Campanha" na página da mesa.
                </p>
              ) : (
                <div className="space-y-2">
                  {catalog.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-xl border border-game-border-primary dark:border-zinc-800 bg-white dark:bg-zinc-800/40 p-2.5"
                    >
                      <div className="text-2xl">{item.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-game-primary dark:text-zinc-100 truncate">{item.name}</div>
                        <div className="text-[11px] text-game-muted">{itemTypeLabel(item.item_type)}</div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleGrant(item)}
                        disabled={!viewedCharId || granting === item.id}
                        className="gap-1 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white"
                        title={viewedName ? `Conceder a ${viewedName}` : 'Selecione um personagem'}
                      >
                        {granting === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Conceder
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
